import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UI_LABELS } from '../lib/terminology';
import { 
  ArrowLeft,
  ArrowRight, 
  Search, 
  Filter, 
  Shapes, 
  Palette, 
  Layers,
  Circle,
  Square,
  Minus,
  RotateCw,
  Grid3X3,
  Target,
  Zap,
  Network,
  FileText,
  BookOpen,
  Eye,
  RefreshCw,
  ToggleLeft,
  Wrench
} from 'lucide-react';

// Shapes Data
const shapes = [
  { id: 'point', name: 'Point', symbol: '•', description: 'Focus, attention, anchor', category: 'shape', isCommitted: true },
  { id: 'line', name: 'Line', symbol: '—', description: 'Connection, boundary, direction', category: 'shape', isCommitted: true },
  { id: 'rectangle', name: 'Rectangle', symbol: '▭', description: 'Container, category, stable structure', category: 'shape', isCommitted: true },
  { id: 'rounded-rectangle', name: 'Rounded Rectangle', symbol: '▢', description: 'Process step, friendly container', category: 'shape', isCommitted: true },
  { id: 'circle', name: 'Circle', symbol: '○', description: 'System, wholeness, completion', category: 'shape', isCommitted: true },
  { id: 'oval', name: 'Oval', symbol: '⬭', description: 'Range, spectrum, flexibility', category: 'shape', isCommitted: false },
  { id: 'triangle', name: 'Triangle', symbol: '△', description: 'Direction, hierarchy, decision point', category: 'shape', isCommitted: true },
  { id: 'diamond', name: 'Diamond', symbol: '◇', description: 'Decision, choice, alternative', category: 'shape', isCommitted: false },
  { id: 'hexagon', name: 'Hexagon', symbol: '⬡', description: 'Facet, capability, multi-dimensional', category: 'shape', isCommitted: false },
  { id: 'curve', name: 'Curve/Arc', symbol: '⌒', description: 'Flow, transition, organic movement', category: 'shape', isCommitted: true },
  { id: 'ring', name: 'Ring/Frame', symbol: '◯', description: 'Boundary, scope, evaluation target', category: 'shape', isCommitted: true }
];

// Color System Data - Universal Design Tool
const colorSystem = {
  // Neutral Colors - Structural and functional
  neutral: [
    { id: 'white', name: 'White', symbol: '■', description: 'Background, space, clarity', category: 'neutral', isCommitted: true, hex: '#FFFFFF' },
    { id: 'light-gray', name: 'Light Gray', symbol: '■', description: 'Subtle backgrounds, dividers', category: 'neutral', isCommitted: true, hex: '#F3F4F6' },
    { id: 'charcoal', name: 'Charcoal', symbol: '■', description: 'Text, hierarchy, structure', category: 'neutral', isCommitted: true, hex: '#9CA3AF' },
    { id: 'dark-gray', name: 'Dark Gray', symbol: '■', description: 'Strong text, emphasis', category: 'neutral', isCommitted: true, hex: '#374151' },
    { id: 'black-color', name: 'Black', symbol: '■', description: 'Maximum contrast, primary text', category: 'neutral', isCommitted: true, hex: '#000000' }
  ],
  
  // Brand Colors - Identity and recognition
  brand: [
    { id: 'primary', name: 'Primary (Sage Green)', symbol: '■', description: 'Brand identity, primary actions', category: 'brand', isCommitted: true, hex: '#5A7D5E' },
    { id: 'sage', name: 'Sage (Alpine Green)', symbol: '■', description: 'Brand secondary, nature themes', category: 'brand', isCommitted: true, hex: '#53675B' },
    { id: 'amber', name: 'Amber (Electric Mustard)', symbol: '■', description: 'Brand accent, energy', category: 'brand', isCommitted: true, hex: '#E3B505' },
    { id: 'coral', name: 'Coral (Luminous Coral)', symbol: '■', description: 'Brand highlight, warmth', category: 'brand', isCommitted: true, hex: '#F85E5E' },
    { id: 'brown', name: 'Brown (Warm Brown)', symbol: '■', description: 'Brand earth tone, grounding', category: 'brand', isCommitted: true, hex: '#584E47' },
    { id: 'deep-ocean-teal', name: 'Deep Ocean Teal', symbol: '■', description: 'Depth, navigation, shadowed water', category: 'brand', isCommitted: true, hex: '#2F5C5B' },
    { id: 'terracotta-clay', name: 'Terracotta Clay', symbol: '■', description: 'Warmth, trail markers, human touch', category: 'brand', isCommitted: true, hex: '#C97A5B' },
    { id: 'sunlit-ochre', name: 'Sunlit Ochre', symbol: '■', description: 'Light, energy, contour highlights', category: 'brand', isCommitted: true, hex: '#E8B86A' },
    { id: 'frosted-sky', name: 'Frosted Sky', symbol: '■', description: 'Atmosphere, altitude, openness', category: 'brand', isCommitted: true, hex: '#AFC6D9' }
  ],
  
  // Color Ramps - Full tonal ranges
  ramps: {
    primary: [
      { id: 'consulting-slate-blue-50', name: 'Primary 50', hex: '#F8F9FB', description: 'Lightest primary tint' },
      { id: 'consulting-slate-blue-100', name: 'Primary 100', hex: '#E8EBF0', description: 'Very light primary' },
      { id: 'consulting-slate-blue-200', name: 'Primary 200', hex: '#D1D7E1', description: 'Light primary' },
      { id: 'consulting-slate-blue-300', name: 'Primary 300', hex: '#B4BECB', description: 'Medium light primary' },
      { id: 'consulting-slate-blue-400', name: 'Primary 400', hex: '#8A9BB0', description: 'Medium primary' },
      { id: 'consulting-slate-blue-500', name: 'Primary 500', hex: '#5A7D5E', description: 'Base primary' },
      { id: 'consulting-slate-blue-600', name: 'Primary 600', hex: '#364063', description: 'Medium dark primary' },
      { id: 'consulting-slate-blue-700', name: 'Primary 700', hex: '#2F3557', description: 'Dark primary' },
      { id: 'consulting-slate-blue-800', name: 'Primary 800', hex: '#282A4B', description: 'Very dark primary' },
      { id: 'consulting-slate-blue-900', name: 'Primary 900', hex: '#211F3F', description: 'Darkest primary shade' }
    ],
    sage: [
      { id: 'sage-50', name: 'Sage 50', hex: '#F6F7F6', description: 'Lightest sage tint' },
      { id: 'sage-100', name: 'Sage 100', hex: '#E8ECE9', description: 'Very light sage' },
      { id: 'sage-200', name: 'Sage 200', hex: '#D1D9D3', description: 'Light sage' },
      { id: 'sage-300', name: 'Sage 300', hex: '#B4C1B7', description: 'Medium light sage' },
      { id: 'sage-400', name: 'Sage 400', hex: '#8AA08F', description: 'Medium sage' },
      { id: 'sage-500', name: 'Sage 500', hex: '#53675B', description: 'Base sage' },
      { id: 'sage-600', name: 'Sage 600', hex: '#4B5C52', description: 'Medium dark sage' },
      { id: 'sage-700', name: 'Sage 700', hex: '#435149', description: 'Dark sage' },
      { id: 'sage-800', name: 'Sage 800', hex: '#3B4640', description: 'Very dark sage' },
      { id: 'sage-900', name: 'Sage 900', hex: '#333B37', description: 'Darkest sage shade' }
    ],
    amber: [
      { id: 'amber-50', name: 'Amber 50', hex: '#FEFBF3', description: 'Lightest amber tint' },
      { id: 'amber-100', name: 'Amber 100', hex: '#FDF4D3', description: 'Very light amber' },
      { id: 'amber-200', name: 'Amber 200', hex: '#FBE8A7', description: 'Light amber' },
      { id: 'amber-300', name: 'Amber 300', hex: '#F8D77B', description: 'Medium light amber' },
      { id: 'amber-400', name: 'Amber 400', hex: '#F4C64F', description: 'Medium amber' },
      { id: 'amber-500', name: 'Amber 500', hex: '#E3B505', description: 'Base amber' },
      { id: 'amber-600', name: 'Amber 600', hex: '#CCA404', description: 'Medium dark amber' },
      { id: 'amber-700', name: 'Amber 700', hex: '#B59303', description: 'Dark amber' },
      { id: 'amber-800', name: 'Amber 800', hex: '#9E8202', description: 'Very dark amber' },
      { id: 'amber-900', name: 'Amber 900', hex: '#877101', description: 'Darkest amber shade' }
    ],
    coral: [
      { id: 'coral-50', name: 'Coral 50', hex: '#FEF2F2', description: 'Lightest coral tint' },
      { id: 'coral-100', name: 'Coral 100', hex: '#FEE2E2', description: 'Very light coral' },
      { id: 'coral-200', name: 'Coral 200', hex: '#FECACA', description: 'Light coral' },
      { id: 'coral-300', name: 'Coral 300', hex: '#FCA5A5', description: 'Medium light coral' },
      { id: 'coral-400', name: 'Coral 400', hex: '#F87171', description: 'Medium coral' },
      { id: 'coral-500', name: 'Coral 500', hex: '#F85E5E', description: 'Base coral' },
      { id: 'coral-600', name: 'Coral 600', hex: '#DC2626', description: 'Medium dark coral' },
      { id: 'coral-700', name: 'Coral 700', hex: '#B91C1C', description: 'Dark coral' },
      { id: 'coral-800', name: 'Coral 800', hex: '#991B1B', description: 'Very dark coral' },
      { id: 'coral-900', name: 'Coral 900', hex: '#7F1D1D', description: 'Darkest coral shade' }
    ],
    brown: [
      { id: 'brown-50', name: 'Brown 50', hex: '#FDF7F0', description: 'Lightest brown tint' },
      { id: 'brown-100', name: 'Brown 100', hex: '#F9EDE3', description: 'Very light brown' },
      { id: 'brown-200', name: 'Brown 200', hex: '#F2D9C7', description: 'Light brown' },
      { id: 'brown-300', name: 'Brown 300', hex: '#E8C4A8', description: 'Medium light brown' },
      { id: 'brown-400', name: 'Brown 400', hex: '#D4A574', description: 'Medium brown' },
      { id: 'brown-500', name: 'Brown 500', hex: '#584E47', description: 'Base brown' },
      { id: 'brown-600', name: 'Brown 600', hex: '#4A4039', description: 'Medium dark brown' },
      { id: 'brown-700', name: 'Brown 700', hex: '#3C322B', description: 'Dark brown' },
      { id: 'brown-800', name: 'Brown 800', hex: '#2E241D', description: 'Very dark brown' },
      { id: 'brown-900', name: 'Brown 900', hex: '#20160F', description: 'Darkest brown shade' }
    ],
    // Complementary Color Ramps
    complementBlue: [
      { id: 'complement-blue-50', name: 'Complement Blue 50', hex: '#EFF6FF', description: 'Lightest complement blue' },
      { id: 'complement-blue-100', name: 'Complement Blue 100', hex: '#DBEAFE', description: 'Very light complement blue' },
      { id: 'complement-blue-200', name: 'Complement Blue 200', hex: '#BFDBFE', description: 'Light complement blue' },
      { id: 'complement-blue-300', name: 'Complement Blue 300', hex: '#93C5FD', description: 'Medium light complement blue' },
      { id: 'complement-blue-400', name: 'Complement Blue 400', hex: '#60A5FA', description: 'Medium complement blue' },
      { id: 'complement-blue-500', name: 'Complement Blue 500', hex: '#1E40AF', description: 'Base complement blue' },
      { id: 'complement-blue-600', name: 'Complement Blue 600', hex: '#1D4ED8', description: 'Medium dark complement blue' },
      { id: 'complement-blue-700', name: 'Complement Blue 700', hex: '#1E3A8A', description: 'Dark complement blue' },
      { id: 'complement-blue-800', name: 'Complement Blue 800', hex: '#1E40AF', description: 'Very dark complement blue' },
      { id: 'complement-blue-900', name: 'Complement Blue 900', hex: '#1E3A8A', description: 'Darkest complement blue' }
    ],
    complementRed: [
      { id: 'complement-red-50', name: 'Complement Red 50', hex: '#FEF2F2', description: 'Lightest complement red' },
      { id: 'complement-red-100', name: 'Complement Red 100', hex: '#FEE2E2', description: 'Very light complement red' },
      { id: 'complement-red-200', name: 'Complement Red 200', hex: '#FECACA', description: 'Light complement red' },
      { id: 'complement-red-300', name: 'Complement Red 300', hex: '#FCA5A5', description: 'Medium light complement red' },
      { id: 'complement-red-400', name: 'Complement Red 400', hex: '#F87171', description: 'Medium complement red' },
      { id: 'complement-red-500', name: 'Complement Red 500', hex: '#DC2626', description: 'Base complement red' },
      { id: 'complement-red-600', name: 'Complement Red 600', hex: '#B91C1C', description: 'Medium dark complement red' },
      { id: 'complement-red-700', name: 'Complement Red 700', hex: '#991B1B', description: 'Dark complement red' },
      { id: 'complement-red-800', name: 'Complement Red 800', hex: '#7F1D1D', description: 'Very dark complement red' },
      { id: 'complement-red-900', name: 'Complement Red 900', hex: '#450A0A', description: 'Darkest complement red' }
    ],
    complementOrange: [
      { id: 'complement-orange-50', name: 'Complement Orange 50', hex: '#FFF7ED', description: 'Lightest complement orange' },
      { id: 'complement-orange-100', name: 'Complement Orange 100', hex: '#FFEDD5', description: 'Very light complement orange' },
      { id: 'complement-orange-200', name: 'Complement Orange 200', hex: '#FED7AA', description: 'Light complement orange' },
      { id: 'complement-orange-300', name: 'Complement Orange 300', hex: '#FDBA74', description: 'Medium light complement orange' },
      { id: 'complement-orange-400', name: 'Complement Orange 400', hex: '#FB923C', description: 'Medium complement orange' },
      { id: 'complement-orange-500', name: 'Complement Orange 500', hex: '#EA580C', description: 'Base complement orange' },
      { id: 'complement-orange-600', name: 'Complement Orange 600', hex: '#C2410C', description: 'Medium dark complement orange' },
      { id: 'complement-orange-700', name: 'Complement Orange 700', hex: '#9A3412', description: 'Dark complement orange' },
      { id: 'complement-orange-800', name: 'Complement Orange 800', hex: '#7C2D12', description: 'Very dark complement orange' },
      { id: 'complement-orange-900', name: 'Complement Orange 900', hex: '#431407', description: 'Darkest complement orange' }
    ],
    complementTeal: [
      { id: 'complement-teal-50', name: 'Complement Teal 50', hex: '#F0FDFA', description: 'Lightest complement teal' },
      { id: 'complement-teal-100', name: 'Complement Teal 100', hex: '#CCFBF1', description: 'Very light complement teal' },
      { id: 'complement-teal-200', name: 'Complement Teal 200', hex: '#99F6E4', description: 'Light complement teal' },
      { id: 'complement-teal-300', name: 'Complement Teal 300', hex: '#5EEAD4', description: 'Medium light complement teal' },
      { id: 'complement-teal-400', name: 'Complement Teal 400', hex: '#2DD4BF', description: 'Medium complement teal' },
      { id: 'complement-teal-500', name: 'Complement Teal 500', hex: '#0D9488', description: 'Base complement teal' },
      { id: 'complement-teal-600', name: 'Complement Teal 600', hex: '#0F766E', description: 'Medium dark complement teal' },
      { id: 'complement-teal-700', name: 'Complement Teal 700', hex: '#115E59', description: 'Dark complement teal' },
      { id: 'complement-teal-800', name: 'Complement Teal 800', hex: '#134E4A', description: 'Very dark complement teal' },
      { id: 'complement-teal-900', name: 'Complement Teal 900', hex: '#042F2E', description: 'Darkest complement teal' }
    ],
    complementBlueGray: [
      { id: 'complement-blue-gray-50', name: 'Complement Blue-Gray 50', hex: '#F8FAFC', description: 'Lightest complement blue-gray' },
      { id: 'complement-blue-gray-100', name: 'Complement Blue-Gray 100', hex: '#F1F5F9', description: 'Very light complement blue-gray' },
      { id: 'complement-blue-gray-200', name: 'Complement Blue-Gray 200', hex: '#E2E8F0', description: 'Light complement blue-gray' },
      { id: 'complement-blue-gray-300', name: 'Complement Blue-Gray 300', hex: '#CBD5E1', description: 'Medium light complement blue-gray' },
      { id: 'complement-blue-gray-400', name: 'Complement Blue-Gray 400', hex: '#94A3B8', description: 'Medium complement blue-gray' },
      { id: 'complement-blue-gray-500', name: 'Complement Blue-Gray 500', hex: '#475569', description: 'Base complement blue-gray' },
      { id: 'complement-blue-gray-600', name: 'Complement Blue-Gray 600', hex: '#334155', description: 'Medium dark complement blue-gray' },
      { id: 'complement-blue-gray-700', name: 'Complement Blue-Gray 700', hex: '#1E293B', description: 'Dark complement blue-gray' },
      { id: 'complement-blue-gray-800', name: 'Complement Blue-Gray 800', hex: '#0F172A', description: 'Very dark complement blue-gray' },
      { id: 'complement-blue-gray-900', name: 'Complement Blue-Gray 900', hex: '#020617', description: 'Darkest complement blue-gray' }
    ],
    // Structural Gap Filler Ramps
    warmGray: [
      { id: 'warm-gray-50', name: 'Warm Gray 50', hex: '#FAFAF9', description: 'Lightest warm gray' },
      { id: 'warm-gray-100', name: 'Warm Gray 100', hex: '#F5F5F4', description: 'Very light warm gray' },
      { id: 'warm-gray-200', name: 'Warm Gray 200', hex: '#E7E5E4', description: 'Light warm gray' },
      { id: 'warm-gray-300', name: 'Warm Gray 300', hex: '#D6D3D1', description: 'Medium light warm gray' },
      { id: 'warm-gray-400', name: 'Warm Gray 400', hex: '#A8A29E', description: 'Medium warm gray' },
      { id: 'warm-gray-500', name: 'Warm Gray 500', hex: '#78716C', description: 'Base warm gray' },
      { id: 'warm-gray-600', name: 'Warm Gray 600', hex: '#57534E', description: 'Medium dark warm gray' },
      { id: 'warm-gray-700', name: 'Warm Gray 700', hex: '#44403C', description: 'Dark warm gray' },
      { id: 'warm-gray-800', name: 'Warm Gray 800', hex: '#292524', description: 'Very dark warm gray' },
      { id: 'warm-gray-900', name: 'Warm Gray 900', hex: '#1C1917', description: 'Darkest warm gray' }
    ],
    coolGray: [
      { id: 'cool-gray-50', name: 'Cool Gray 50', hex: '#F8FAFC', description: 'Lightest cool gray' },
      { id: 'cool-gray-100', name: 'Cool Gray 100', hex: '#F1F5F9', description: 'Very light cool gray' },
      { id: 'cool-gray-200', name: 'Cool Gray 200', hex: '#E2E8F0', description: 'Light cool gray' },
      { id: 'cool-gray-300', name: 'Cool Gray 300', hex: '#CBD5E1', description: 'Medium light cool gray' },
      { id: 'cool-gray-400', name: 'Cool Gray 400', hex: '#94A3B8', description: 'Medium cool gray' },
      { id: 'cool-gray-500', name: 'Cool Gray 500', hex: '#64748B', description: 'Base cool gray' },
      { id: 'cool-gray-600', name: 'Cool Gray 600', hex: '#475569', description: 'Medium dark cool gray' },
      { id: 'cool-gray-700', name: 'Cool Gray 700', hex: '#334155', description: 'Dark cool gray' },
      { id: 'cool-gray-800', name: 'Cool Gray 800', hex: '#1E293B', description: 'Very dark cool gray' },
      { id: 'cool-gray-900', name: 'Cool Gray 900', hex: '#0F172A', description: 'Darkest cool gray' }
    ],
    midBlue: [
      { id: 'mid-blue-50', name: 'Mid Blue 50', hex: '#F8FAFC', description: 'Lightest mid blue' },
      { id: 'mid-blue-100', name: 'Mid Blue 100', hex: '#F1F5F9', description: 'Very light mid blue' },
      { id: 'mid-blue-200', name: 'Mid Blue 200', hex: '#E2E8F0', description: 'Light mid blue' },
      { id: 'mid-blue-300', name: 'Mid Blue 300', hex: '#CBD5E1', description: 'Medium light mid blue' },
      { id: 'mid-blue-400', name: 'Mid Blue 400', hex: '#94A3B8', description: 'Medium mid blue' },
      { id: 'mid-blue-500', name: 'Mid Blue 500', hex: '#64748B', description: 'Base mid blue' },
      { id: 'mid-blue-600', name: 'Mid Blue 600', hex: '#475569', description: 'Medium dark mid blue' },
      { id: 'mid-blue-700', name: 'Mid Blue 700', hex: '#334155', description: 'Dark mid blue' },
      { id: 'mid-blue-800', name: 'Mid Blue 800', hex: '#1E293B', description: 'Very dark mid blue' },
      { id: 'mid-blue-900', name: 'Mid Blue 900', hex: '#0F172A', description: 'Darkest mid blue' }
    ],
    midGreen: [
      { id: 'mid-green-50', name: 'Mid Green 50', hex: '#F9FAFB', description: 'Lightest mid green' },
      { id: 'mid-green-100', name: 'Mid Green 100', hex: '#F3F4F6', description: 'Very light mid green' },
      { id: 'mid-green-200', name: 'Mid Green 200', hex: '#E5E7EB', description: 'Light mid green' },
      { id: 'mid-green-300', name: 'Mid Green 300', hex: '#D1D5DB', description: 'Medium light mid green' },
      { id: 'mid-green-400', name: 'Mid Green 400', hex: '#9CA3AF', description: 'Medium mid green' },
      { id: 'mid-green-500', name: 'Mid Green 500', hex: '#6B7280', description: 'Base mid green' },
      { id: 'mid-green-600', name: 'Mid Green 600', hex: '#4B5563', description: 'Medium dark mid green' },
      { id: 'mid-green-700', name: 'Mid Green 700', hex: '#374151', description: 'Dark mid green' },
      { id: 'mid-green-800', name: 'Mid Green 800', hex: '#1F2937', description: 'Very dark mid green' },
      { id: 'mid-green-900', name: 'Mid Green 900', hex: '#111827', description: 'Darkest mid green' }
    ],
    midYellow: [
      { id: 'mid-yellow-50', name: 'Mid Yellow 50', hex: '#FFFBEB', description: 'Lightest mid yellow' },
      { id: 'mid-yellow-100', name: 'Mid Yellow 100', hex: '#FEF3C7', description: 'Very light mid yellow' },
      { id: 'mid-yellow-200', name: 'Mid Yellow 200', hex: '#FDE68A', description: 'Light mid yellow' },
      { id: 'mid-yellow-300', name: 'Mid Yellow 300', hex: '#FCD34D', description: 'Medium light mid yellow' },
      { id: 'mid-yellow-400', name: 'Mid Yellow 400', hex: '#FBBF24', description: 'Medium mid yellow' },
      { id: 'mid-yellow-500', name: 'Mid Yellow 500', hex: '#D97706', description: 'Base mid yellow' },
      { id: 'mid-yellow-600', name: 'Mid Yellow 600', hex: '#B45309', description: 'Medium dark mid yellow' },
      { id: 'mid-yellow-700', name: 'Mid Yellow 700', hex: '#92400E', description: 'Dark mid yellow' },
      { id: 'mid-yellow-800', name: 'Mid Yellow 800', hex: '#78350F', description: 'Very dark mid yellow' },
      { id: 'mid-yellow-900', name: 'Mid Yellow 900', hex: '#451A03', description: 'Darkest mid yellow' }
    ]
  },
  
  // Complementary Colors - Color theory based
  complements: [
    { id: 'complement-blue', name: 'Complement Blue', symbol: '■', description: 'Complementary to amber (yellow-orange)', category: 'complement', isCommitted: true, hex: '#1E40AF' },
    { id: 'complement-red', name: 'Complement Red', symbol: '■', description: 'Complementary to sage (green)', category: 'complement', isCommitted: true, hex: '#DC2626' },
    { id: 'complement-orange', name: 'Complement Orange', symbol: '■', description: 'Complementary to primary (blue)', category: 'complement', isCommitted: true, hex: '#EA580C' },
    { id: 'complement-teal', name: 'Complement Teal', symbol: '■', description: 'Complementary to coral (red-pink)', category: 'complement', isCommitted: true, hex: '#0D9488' },
    { id: 'complement-blue-gray', name: 'Complement Blue-Gray', symbol: '■', description: 'Complementary to brown (warm neutral)', category: 'complement', isCommitted: true, hex: '#475569' }
  ],
  
  // Structural Gap Fillers - Colors that bridge gaps in the palette
  structural: [
    { id: 'burnt-orange', name: 'Burnt Orange', symbol: '■', description: 'Natural bridge between Mustard and Coral', category: 'structural', isCommitted: true, hex: '#D86B2D' },
    { id: 'olive-drab-green', name: 'Olive Drab Green', symbol: '■', description: 'Neutral midtone for vegetation and field texture', category: 'structural', isCommitted: true, hex: '#6C7142' },
    { id: 'muted-violet-gray', name: 'Muted Violet Gray', symbol: '■', description: 'Subtle complement to Sage Green', category: 'structural', isCommitted: true, hex: '#756D8A' },
    { id: 'dusty-sand-beige', name: 'Dusty Sand Beige', symbol: '■', description: 'Light neutral for terrain, parchment, or skin', category: 'structural', isCommitted: true, hex: '#D8C6A8' }
  ]
};

// Flatten colors for easy access
const colors = [
  ...colorSystem.neutral, 
  ...colorSystem.brand, 
  ...colorSystem.complements,
  ...colorSystem.structural,
  ...(colorSystem.ramps ? Object.values(colorSystem.ramps).flat() : [])
];

// Line Styles Data
const lineStyles = [
  { id: 'solid', name: 'Solid', symbol: '—', description: 'Strong connection, primary relationship, active', category: 'line', isCommitted: true },
  { id: 'dashed', name: 'Dashed', symbol: '- - -', description: 'Secondary relationship, optional, conditional', category: 'line', isCommitted: true },
  { id: 'dotted', name: 'Dotted', symbol: '· · ·', description: 'Weak connection, suggestion, potential', category: 'line', isCommitted: true },
  { id: 'double', name: 'Double', symbol: '═', description: 'Governance, override, strong constraint', category: 'line', isCommitted: true },
  { id: 'thick', name: 'Thick', symbol: '▬', description: 'Emphasis, primary flow, critical', category: 'line', isCommitted: true },
  { id: 'thin-line', name: 'Thin', symbol: '—', description: 'Detail, secondary information, subtle', category: 'line', isCommitted: true },
  { id: 'wavy', name: 'Wavy', symbol: '⌇', description: 'Organic, natural, flexible', category: 'line', isCommitted: false },
  { id: 'zigzag', name: 'Zigzag', symbol: '⚡', description: 'Disruption, change, transformation', category: 'line', isCommitted: false }
];

// Fill Patterns Data
const fillPatterns = [
  { id: 'solid-fill', name: 'Solid Fill', symbol: '■', description: 'Complete, finished, active', category: 'fill', isCommitted: true },
  { id: 'outline', name: 'Outline', symbol: '□', description: 'Incomplete, draft, potential', category: 'fill', isCommitted: true },
  { id: 'dotted-fill', name: 'Dotted Fill', symbol: '▫', description: 'Partial, in progress, developing', category: 'fill', isCommitted: true },
  { id: 'dashed-fill', name: 'Dashed Fill', symbol: '▭', description: 'Conditional, temporary, provisional', category: 'fill', isCommitted: true },
  { id: 'diagonal-lines', name: 'Diagonal Lines', symbol: '╱', description: 'Movement, flow, direction', category: 'fill', isCommitted: false },
  { id: 'vertical-lines', name: 'Vertical Lines', symbol: '│', description: 'Structure, hierarchy, organization', category: 'fill', isCommitted: false },
  { id: 'horizontal-lines', name: 'Horizontal Lines', symbol: '─', description: 'Stability, foundation, base', category: 'fill', isCommitted: false },
  { id: 'crosshatch', name: 'Crosshatch', symbol: '╬', description: 'Complex, integrated, multi-dimensional', category: 'fill', isCommitted: false },
  { id: 'dots', name: 'Dots', symbol: '⋯', description: 'Granular, detailed, specific', category: 'fill', isCommitted: false },
  { id: 'gradient', name: 'Gradient', symbol: '▬', description: 'Spectrum, range, transition', category: 'fill', isCommitted: false }
];

// Shape Modifiers Data
const shapeModifiers = [
  { id: 'corner-sharp', name: 'Sharp Corners', symbol: '▭', description: 'Technical, precise, formal', category: 'modifier', isCommitted: true },
  { id: 'corner-rounded', name: 'Rounded Corners', symbol: '▢', description: 'Friendly, approachable, process', category: 'modifier', isCommitted: true },
  { id: 'corner-very-rounded', name: 'Very Rounded', symbol: '◯', description: 'Organic, natural, complete', category: 'modifier', isCommitted: false },
  { id: 'size-large', name: 'Large Size', symbol: '⬜', description: 'Primary, important, focal', category: 'modifier', isCommitted: true },
  { id: 'size-medium', name: 'Medium Size', symbol: '▭', description: 'Secondary, supporting, adjacent', category: 'modifier', isCommitted: true },
  { id: 'size-small', name: 'Small Size', symbol: '▫', description: 'Detail, utility, peripheral', category: 'modifier', isCommitted: true },
  { id: 'orientation-upright', name: 'Upright', symbol: '▭', description: 'Stable, established, standard', category: 'modifier', isCommitted: true },
  { id: 'orientation-tilted', name: 'Tilted', symbol: '⬢', description: 'Dynamic, changing, transitional', category: 'modifier', isCommitted: false },
  { id: 'orientation-rotated', name: 'Rotated', symbol: '⬡', description: 'Alternative, different perspective', category: 'modifier', isCommitted: false }
];

// Composite Patterns Data
const compositePatterns = [
  { id: 'grid-3x3', name: '3x3 Grid', symbol: '⬜⬜⬜\n⬜⬜⬜\n⬜⬜⬜', description: 'Nine-part system, comprehensive coverage', category: 'composite', isCommitted: true },
  { id: 'grid-2x2', name: '2x2 Grid', symbol: '⬜⬜\n⬜⬜', description: 'Four-quadrant analysis, balanced view', category: 'composite', isCommitted: true },
  { id: 'linear-grid', name: 'Linear Grid', symbol: '⬜⬜⬜⬜', description: 'Sequential, ordered, step-by-step', category: 'composite', isCommitted: true },
  { id: 'radial-grid', name: 'Radial Grid', symbol: '⬜\n⬜⬜⬜\n⬜', description: 'Central focus, radiating outward', category: 'composite', isCommitted: false },
  { id: 'quarter-circle', name: 'Quarter Circle', symbol: '◐', description: 'Partial completion, 25% progress', category: 'composite', isCommitted: true },
  { id: 'half-circle', name: 'Half Circle', symbol: '◑', description: 'Half complete, 50% progress', category: 'composite', isCommitted: true },
  { id: 'three-quarter-circle', name: 'Three-Quarter Circle', symbol: '◒', description: 'Nearly complete, 75% progress', category: 'composite', isCommitted: true },
  { id: 'full-circle', name: 'Full Circle', symbol: '●', description: 'Complete, 100% progress', category: 'composite', isCommitted: true },
  { id: 'concentric-circles', name: 'Concentric Circles', symbol: '◯◯◯', description: 'Layers, depth, hierarchy', category: 'composite', isCommitted: true },
  { id: 'nested-rectangles', name: 'Nested Rectangles', symbol: '▭▭▭', description: 'Containment, part-of relationships', category: 'composite', isCommitted: true },
  { id: 'stacked-elements', name: 'Stacked Elements', symbol: '▭\n▭\n▭', description: 'Sequential, layered, building', category: 'composite', isCommitted: true },
  { id: 'overlapping-elements', name: 'Overlapping Elements', symbol: '⬜⬜', description: 'Integration, intersection, collaboration', category: 'composite', isCommitted: false }
];

// Patterns Data (from Visual Design Grammar)
const patterns = [
  { id: 'hierarchical', name: 'Hierarchical', symbol: '⬜⬜⬜\n⬜⬜⬜\n⬜⬜⬜', description: 'Levels of influence, hierarchy, influence cascades', category: 'pattern', isCommitted: true },
  { id: 'matrix', name: 'Matrix', symbol: '⬜⬜\n⬜⬜', description: 'Two-dimensional analysis, relationships between dimensions', category: 'pattern', isCommitted: true },
  { id: 'flow', name: 'Flow', symbol: '⬜→⬜→⬜', description: 'Process flow, sequence, movement', category: 'pattern', isCommitted: true },
  { id: 'nested', name: 'Nested', symbol: '◯◯◯', description: 'Containment, layers, nested systems', category: 'pattern', isCommitted: true }
];

// Connections Data (from Visual Design Grammar)
const connections = [
  { id: 'direct', name: 'Direct', symbol: '→', description: 'Direct cause → effect relationships', category: 'connection', isCommitted: true },
  { id: 'feedback', name: 'Feedback', symbol: '↻', description: 'Circular influence, feedback mechanisms', category: 'connection', isCommitted: true },
  { id: 'influence', name: 'Influence', symbol: '↓', description: 'Indirect influence, suggestions', category: 'connection', isCommitted: true },
  { id: 'threshold', name: 'Threshold', symbol: '|', description: 'Boundaries, thresholds, transition points', category: 'connection', isCommitted: true }
];

// Semantic Combinations Data
const semanticCombinations = [
  { id: 'status-complete', name: 'Complete Status', symbol: '■', description: 'Complete, active, ready', category: 'semantic', isCommitted: true },
  { id: 'status-incomplete', name: 'Incomplete Status', symbol: '□', description: 'Incomplete, draft, potential', category: 'semantic', isCommitted: true },
  { id: 'status-progress', name: 'In Progress', symbol: '▫', description: 'In progress, developing, partial', category: 'semantic', isCommitted: true },
  { id: 'status-conditional', name: 'Conditional', symbol: '▭', description: 'Conditional, temporary, provisional', category: 'semantic', isCommitted: true },
  { id: 'status-complex', name: 'Complex', symbol: '╬', description: 'Complex, integrated, multi-faceted', category: 'semantic', isCommitted: false },
  { id: 'process-complete', name: 'Process Complete', symbol: '●', description: 'Complete, finished, closed', category: 'semantic', isCommitted: true },
  { id: 'process-open', name: 'Process Open', symbol: '○', description: 'Open, available, potential', category: 'semantic', isCommitted: true },
  { id: 'relationship-strong', name: 'Strong Relationship', symbol: '—', description: 'Strong, direct, primary', category: 'semantic', isCommitted: true },
  { id: 'relationship-conditional', name: 'Conditional Relationship', symbol: '- - -', description: 'Conditional, secondary, optional', category: 'semantic', isCommitted: true },
  { id: 'relationship-weak', name: 'Weak Relationship', symbol: '· · ·', description: 'Weak, suggested, potential', category: 'semantic', isCommitted: true }
];

// Relationship Encoding System
const relationshipTypes = [
  // Hierarchical Relationships
  { id: 'is-a', name: 'Is-A', symbol: '⊃', description: 'Instance-of, type-of relationship', category: 'hierarchical', isCommitted: true },
  { id: 'part-of', name: 'Part-Of', symbol: '⊂', description: 'Contained-by relationship', category: 'hierarchical', isCommitted: true },
  { id: 'contains', name: 'Contains', symbol: '⊃', description: 'Has-part relationship', category: 'hierarchical', isCommitted: true },
  { id: 'specializes', name: 'Specializes', symbol: '→', description: 'More specific version of', category: 'hierarchical', isCommitted: true },
  { id: 'generalizes', name: 'Generalizes', symbol: '←', description: 'More general version of', category: 'hierarchical', isCommitted: true },
  { id: 'is-level-of', name: 'Is-Level-Of', symbol: '↕', description: 'Different abstraction level', category: 'hierarchical', isCommitted: true },
  
  // Functional Relationships
  { id: 'applies-to', name: 'Applies-To', symbol: '→', description: 'Application relationship', category: 'functional', isCommitted: true },
  { id: 'used-by', name: 'Used-By', symbol: '←', description: 'Usage relationship', category: 'functional', isCommitted: true },
  { id: 'requires', name: 'Requires', symbol: '→', description: 'Dependency requirement', category: 'functional', isCommitted: true },
  { id: 'enables', name: 'Enables', symbol: '→', description: 'Capability enablement', category: 'functional', isCommitted: true },
  { id: 'supports', name: 'Supports', symbol: '→', description: 'Support relationship', category: 'functional', isCommitted: true },
  { id: 'supported-by', name: 'Supported-By', symbol: '←', description: 'Receives support from', category: 'functional', isCommitted: true },
  { id: 'depends-on', name: 'Depends-On', symbol: '→', description: 'Dependency relationship', category: 'functional', isCommitted: true },
  
  // Semantic Relationships
  { id: 'derived-from', name: 'Derived-From', symbol: '↗', description: 'Origin relationship', category: 'semantic', isCommitted: true },
  { id: 'inspired-by', name: 'Inspired-By', symbol: '↗', description: 'Inspiration relationship', category: 'semantic', isCommitted: true },
  { id: 'analogous-to', name: 'Analogous-To', symbol: '≈', description: 'Similarity relationship', category: 'semantic', isCommitted: true },
  { id: 'contrasts-with', name: 'Contrasts-With', symbol: '≠', description: 'Opposition relationship', category: 'semantic', isCommitted: true },
  { id: 'incompatible-with', name: 'Incompatible-With', symbol: '✗', description: 'Incompatibility relationship', category: 'semantic', isCommitted: true },
  
  // Explanatory Relationships
  { id: 'illustrates', name: 'Illustrates', symbol: '→', description: 'Example relationship', category: 'explanatory', isCommitted: true },
  { id: 'explains', name: 'Explains', symbol: '→', description: 'Explanation relationship', category: 'explanatory', isCommitted: true },
  { id: 'is-example-of', name: 'Is-Example-Of', symbol: '→', description: 'Exemplification relationship', category: 'explanatory', isCommitted: true },
  { id: 'is-variation-of', name: 'Is-Variation-Of', symbol: '~', description: 'Variation relationship', category: 'explanatory', isCommitted: true },
  
  // Referential Relationships
  { id: 'linked-to', name: 'Linked-To', symbol: '↔', description: 'Bidirectional link', category: 'referential', isCommitted: true },
  { id: 'references', name: 'References', symbol: '→', description: 'Reference relationship', category: 'referential', isCommitted: true },
  { id: 'refines', name: 'Refines', symbol: '→', description: 'Refinement relationship', category: 'referential', isCommitted: true },
  { id: 'extends', name: 'Extends', symbol: '→', description: 'Extension relationship', category: 'referential', isCommitted: true },
  { id: 'replaces', name: 'Replaces', symbol: '→', description: 'Replacement relationship', category: 'referential', isCommitted: true },
  { id: 'translates-to', name: 'Translates-To', symbol: '→', description: 'Translation relationship', category: 'referential', isCommitted: true },
  
  // Temporal Relationships
  { id: 'precedes', name: 'Precedes', symbol: '→', description: 'Comes before', category: 'temporal', isCommitted: true },
  { id: 'follows', name: 'Follows', symbol: '←', description: 'Comes after', category: 'temporal', isCommitted: true },
  
  // Structural Relationships
  { id: 'relates-to', name: 'Relates-To', symbol: '↔', description: 'General relationship', category: 'structural-relationship', isCommitted: true },
  { id: 'maps-to', name: 'Maps-To', symbol: '→', description: 'Mapping relationship', category: 'structural-relationship', isCommitted: true },
  { id: 'connected-to', name: 'Connected-To', symbol: '↔', description: 'Connection relationship', category: 'structural-relationship', isCommitted: true },
  { id: 'attaches-to', name: 'Attaches-To', symbol: '→', description: 'Attachment relationship', category: 'structural-relationship', isCommitted: true }
];

// Font Families Data - Actual font selections
const fontFamilies = [
  { id: 'freight-display-pro', name: 'Freight Display Pro', fontFamily: 'freight-display-pro, serif', description: 'Sophisticated serif for headlines and hero text', category: 'font', isCommitted: true },
  { id: 'freight-text-pro', name: 'Freight Text Pro', fontFamily: 'freight-text-pro, serif', description: 'Highly readable serif for body text and extended reading', category: 'font', isCommitted: true },
  { id: 'freight-sans-pro', name: 'Freight Sans Pro', fontFamily: 'freight-sans-pro, sans-serif', description: 'Clean, modern sans-serif from the Freight family', category: 'font', isCommitted: true },
  { id: 'freight-macro-pro', name: 'Freight Macro Pro', fontFamily: 'freight-macro-pro, serif', description: 'Bold, impactful display variant for maximum visual impact', category: 'font', isCommitted: true },
  { id: 'freight-big-pro', name: 'Freight Big Pro', fontFamily: 'freight-big-pro, serif', description: 'Expansive display variant for large-scale applications', category: 'font', isCommitted: true },
  { id: 'fieldwork', name: 'Fieldwork', fontFamily: 'fieldwork, system-ui, sans-serif', description: 'Clean, modern sans-serif for headings and UI labels', category: 'font', isCommitted: true },
  { id: 'degular-variable', name: 'Degular Variable', fontFamily: 'degular, system-ui, sans-serif', description: 'Highly readable sans-serif for body text and content', category: 'font', isCommitted: true },
  { id: 'freight-text-quote', name: 'Freight Text Pro', fontFamily: 'freight-text-pro, Georgia, serif', description: 'Versatile serif for quotations and highlighted content', category: 'font', isCommitted: true },
  { id: 'yorkten-slab', name: 'Yorkten Slab', fontFamily: 'yorkten-slab, serif', description: 'Distinctive slab serif for special headings and accent text', category: 'font', isCommitted: true },
  { id: 'degular-mono', name: 'Degular Mono', fontFamily: 'degular-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', description: 'Monospace font optimized for code and technical content', category: 'font', isCommitted: true }
];

// Typography System Data - Font treatments and properties
const typographySystem = {
  
  // Font Weights - Visual hierarchy and emphasis
  weights: [
    { id: 'thin', name: 'Thin (100)', symbol: 'T', description: 'Delicate, subtle, minimal', category: 'weight', isCommitted: true },
    { id: 'light', name: 'Light (300)', symbol: 'L', description: 'Elegant, refined, gentle', category: 'weight', isCommitted: true },
    { id: 'regular', name: 'Regular (400)', symbol: 'R', description: 'Standard, readable, neutral', category: 'weight', isCommitted: true },
    { id: 'medium', name: 'Medium (500)', symbol: 'M', description: 'Balanced, moderate emphasis', category: 'weight', isCommitted: true },
    { id: 'semibold', name: 'Semibold (600)', symbol: 'S', description: 'Strong, confident, prominent', category: 'weight', isCommitted: true },
    { id: 'bold', name: 'Bold (700)', symbol: 'B', description: 'Emphatic, important, attention-grabbing', category: 'weight', isCommitted: true },
    { id: 'extrabold', name: 'Extra Bold (800)', symbol: 'E', description: 'Powerful, commanding, dramatic', category: 'weight', isCommitted: true },
    { id: 'black', name: 'Black (900)', symbol: 'K', description: 'Maximum impact, boldest statement', category: 'weight', isCommitted: true }
  ],
  
  // Font Styles - Mood and context modifiers
  styles: [
    { id: 'normal-style', name: 'Normal', symbol: 'N', description: 'Standard, neutral, baseline', category: 'style', isCommitted: true },
    { id: 'italic', name: 'Italic', symbol: 'I', description: 'Emphasis, distinction, elegance', category: 'style', isCommitted: true },
    { id: 'oblique', name: 'Oblique', symbol: 'O', description: 'Slanted, dynamic, movement', category: 'style', isCommitted: false },
    { id: 'small-caps', name: 'Small Caps', symbol: 'C', description: 'Formal, traditional, authoritative', category: 'style', isCommitted: false }
  ],
  
  // Font Sizes - Hierarchy and importance
  sizes: [
    { id: 'xs', name: 'Extra Small (12px)', symbol: 'XS', description: 'Fine print, captions, secondary info', category: 'size', isCommitted: true },
    { id: 'sm', name: 'Small (14px)', symbol: 'SM', description: 'Body text, readable, standard', category: 'size', isCommitted: true },
    { id: 'base', name: 'Base (16px)', symbol: 'B', description: 'Primary body text, comfortable reading', category: 'size', isCommitted: true },
    { id: 'lg', name: 'Large (18px)', symbol: 'L', description: 'Emphasized text, subheadings', category: 'size', isCommitted: true },
    { id: 'xl', name: 'Extra Large (20px)', symbol: 'XL', description: 'Section headers, important content', category: 'size', isCommitted: true },
    { id: '2xl', name: '2X Large (24px)', symbol: '2X', description: 'Page headers, major sections', category: 'size', isCommitted: true },
    { id: '3xl', name: '3X Large (30px)', symbol: '3X', description: 'Title text, prominent headings', category: 'size', isCommitted: true },
    { id: '4xl', name: '4X Large (36px)', symbol: '4X', description: 'Hero text, main titles', category: 'size', isCommitted: true },
    { id: '5xl', name: '5X Large (48px)', symbol: '5X', description: 'Display text, maximum impact', category: 'size', isCommitted: true }
  ],
  
  // Text Decorations - Additional semantic meaning
  decorations: [
    { id: 'none-decoration', name: 'None', symbol: '—', description: 'Clean, unadorned, standard', category: 'decoration', isCommitted: true },
    { id: 'underline', name: 'Underline', symbol: 'U', description: 'Emphasis, links, importance', category: 'decoration', isCommitted: true },
    { id: 'overline', name: 'Overline', symbol: 'O', description: 'Distinction, special marking', category: 'decoration', isCommitted: false },
    { id: 'line-through', name: 'Strikethrough', symbol: 'S', description: 'Deleted, obsolete, crossed out', category: 'decoration', isCommitted: true },
    { id: 'double-underline', name: 'Double Underline', symbol: 'D', description: 'Strong emphasis, critical', category: 'decoration', isCommitted: false }
  ],
  
  // Letter Spacing - Density and readability
  spacing: [
    { id: 'tighter', name: 'Tighter (-0.05em)', symbol: 'T', description: 'Condensed, dense, technical', category: 'spacing', isCommitted: true },
    { id: 'tight-spacing', name: 'Tight (-0.025em)', symbol: 't', description: 'Slightly condensed, efficient', category: 'spacing', isCommitted: true },
    { id: 'normal-spacing', name: 'Normal (0em)', symbol: 'N', description: 'Standard, balanced, readable', category: 'spacing', isCommitted: true },
    { id: 'wide', name: 'Wide (0.025em)', symbol: 'w', description: 'Expanded, elegant, spacious', category: 'spacing', isCommitted: true },
    { id: 'wider', name: 'Wider (0.05em)', symbol: 'W', description: 'Very expanded, dramatic, display', category: 'spacing', isCommitted: true }
  ],
  
  // Line Heights - Vertical rhythm and readability
  lineHeights: [
    { id: 'none-lineheight', name: 'None (1)', symbol: 'N', description: 'Tight, compact, dense', category: 'lineHeight', isCommitted: true },
    { id: 'tight-lineheight', name: 'Tight (1.25)', symbol: 'T', description: 'Condensed, efficient, technical', category: 'lineHeight', isCommitted: true },
    { id: 'snug', name: 'Snug (1.375)', symbol: 'S', description: 'Slightly tight, balanced', category: 'lineHeight', isCommitted: true },
    { id: 'normal-lineheight', name: 'Normal (1.5)', symbol: 'N', description: 'Standard, comfortable, readable', category: 'lineHeight', isCommitted: true },
    { id: 'relaxed', name: 'Relaxed (1.625)', symbol: 'R', description: 'Spacious, elegant, comfortable', category: 'lineHeight', isCommitted: true },
    { id: 'loose', name: 'Loose (2)', symbol: 'L', description: 'Very spacious, dramatic, display', category: 'lineHeight', isCommitted: true }
  ]
};

// Typography Elements (flattened for easy access)
const typographyElements = [
  ...(typographySystem.weights || []),
  ...(typographySystem.styles || []),
  ...(typographySystem.sizes || []),
  ...(typographySystem.decorations || []),
  ...(typographySystem.spacing || []),
  ...(typographySystem.lineHeights || [])
];

// Line/Shape Elements (Basic Elements Only)
const lineShapeElements = [
  ...(shapes || []),
  ...(lineStyles || []),
  ...(fillPatterns || []),
  ...(shapeModifiers || []),
  ...(connections || [])
];

// Color Elements
const colorElements = [
  ...(colors || [])
];


// Categories for filtering line/shape elements
const lineShapeCategories = [
  { id: 'all', name: 'All Line/Shape Elements', icon: Shapes },
  { id: 'shape', name: 'Shapes', icon: Circle },
  { id: 'line', name: 'Line Styles', icon: Minus },
  { id: 'fill', name: 'Fill Patterns', icon: Square },
  { id: 'modifier', name: 'Shape Modifiers', icon: RotateCw },
  { id: 'connection', name: 'Connections', icon: Network }
];

// Categories for filtering color elements
const colorCategories = [
  { id: 'all', name: 'All Colors', icon: Palette },
  { id: 'neutral', name: 'Neutral Colors', icon: Palette },
  { id: 'brand', name: 'Brand Colors', icon: Palette },
  { id: 'complement', name: 'Complementary Colors', icon: Palette },
  { id: 'structural', name: 'Structural Colors', icon: Palette }
];

// Categories for filtering font families
const fontCategories = [
  { id: 'all', name: 'All Fonts', icon: FileText },
  { id: 'font', name: 'Font Families', icon: Circle }
];

// Categories for filtering typography elements
const typographyCategories = [
  { id: 'all', name: 'All Typography', icon: FileText },
  { id: 'weight', name: 'Font Weights', icon: Square },
  { id: 'style', name: 'Font Styles', icon: Minus },
  { id: 'size', name: 'Font Sizes', icon: Target },
  { id: 'decoration', name: 'Text Decorations', icon: Network },
  { id: 'spacing', name: 'Letter Spacing', icon: Grid3X3 },
  { id: 'lineHeight', name: 'Line Heights', icon: Zap }
];

// Categories for filtering relationship types
const relationshipCategories = [
  { id: 'all', name: 'All Relationships', icon: Network },
  { id: 'hierarchical', name: 'Hierarchical', icon: Target },
  { id: 'functional', name: 'Functional', icon: Zap },
  { id: 'semantic', name: 'Semantic', icon: Circle },
  { id: 'explanatory', name: 'Explanatory', icon: Square },
  { id: 'referential', name: 'Referential', icon: Network },
  { id: 'temporal', name: 'Temporal', icon: Minus },
  { id: 'structural-relationship', name: 'Structural', icon: Grid3X3 }
];

// Structure Types Data - Visual organization patterns
const structureTypes = [
  // Hierarchical Structures
  { id: 'hierarchy-tree', name: 'Hierarchy / Tree', symbol: '🌳', description: 'Parent-child relationships, organizational charts', category: 'hierarchical', isCommitted: true },
  { id: 'taxonomy', name: 'Taxonomy', symbol: '📊', description: 'Classification structure, categorical organization', category: 'hierarchical', isCommitted: true },
  { id: 'ontology', name: 'Ontology', symbol: '🔗', description: 'Network of types and relationships', category: 'hierarchical', isCommitted: true },
  { id: 'pyramid', name: 'Pyramid', symbol: '🔺', description: 'Layered hierarchy with decreasing scope', category: 'hierarchical', isCommitted: true },
  { id: 'tree-map', name: 'Tree Map / Sunburst', symbol: '☀️', description: 'Hierarchical data visualization', category: 'hierarchical', isCommitted: true },
  
  // Network Structures
  { id: 'network-graph', name: 'Network / Graph', symbol: '🕸️', description: 'Interconnected nodes and relationships', category: 'network', isCommitted: true },
  { id: 'constellation', name: 'Constellation', symbol: '⭐', description: 'Connected points forming patterns', category: 'network', isCommitted: true },
  { id: 'map', name: 'Map (literal or metaphorical)', symbol: '🗺️', description: 'Spatial or conceptual mapping', category: 'network', isCommitted: true },
  
  // Sequential Structures
  { id: 'sequence-ordered', name: 'Sequence / Ordered List', symbol: '➡️', description: 'Linear progression, step-by-step', category: 'sequential', isCommitted: true },
  { id: 'flow-process', name: 'Flow / Process Flow / Swimlane', symbol: '🌊', description: 'Process visualization with lanes', category: 'sequential', isCommitted: true },
  { id: 'cycle-loop', name: 'Cycle / Loop', symbol: '🔄', description: 'Circular processes, iterative patterns', category: 'sequential', isCommitted: true },
  { id: 'phased-process', name: 'Phased Process (Stepwise)', symbol: '📋', description: 'Staged progression with milestones', category: 'sequential', isCommitted: true },
  { id: 'spiral-helix', name: 'Spiral / Helix', symbol: '🌀', description: 'Progressive circular development', category: 'sequential', isCommitted: true },
  
  // Collection Structures
  { id: 'unordered-list', name: 'Unordered List / Collection / Set', symbol: '📝', description: 'Grouped items without hierarchy', category: 'collection', isCommitted: true },
  { id: 'checklist', name: 'Checklist', symbol: '✅', description: 'Task-oriented item lists', category: 'collection', isCommitted: true },
  { id: 'card-deck', name: 'Card Deck / Modular Units', symbol: '🃏', description: 'Independent, reusable components', category: 'collection', isCommitted: true },
  
  // Matrix Structures
  { id: 'matrix-2d', name: 'Matrix (2D grid)', symbol: '📊', description: 'Two-dimensional analysis grid', category: 'matrix', isCommitted: true },
  { id: 'table', name: 'Table', symbol: '📋', description: 'Structured data in rows and columns', category: 'matrix', isCommitted: true },
  { id: 'faceted', name: 'Faceted / Multi-Dimensional', symbol: '🔲', description: 'Multiple attribute dimensions', category: 'matrix', isCommitted: true },
  
  // Layered Structures
  { id: 'layered-nested', name: 'Layered / Nested / Tiered', symbol: '🥞', description: 'Stacked levels of information', category: 'layered', isCommitted: true },
  { id: 'canvas', name: 'Canvas (visual workspace)', symbol: '🎨', description: 'Open visual working space', category: 'layered', isCommitted: true },
  
  // State Structures
  { id: 'continuum-range', name: 'Continuum / Range / Spectrum', symbol: '📏', description: 'Gradual progression between states', category: 'state', isCommitted: true },
  { id: 'toggle-binary', name: 'Toggle / Binary / Either–Or', symbol: '🔄', description: 'Two-state switching mechanism', category: 'state', isCommitted: true },
  { id: 'multi-state', name: 'Multi-State / Mode-Based', symbol: '🎛️', description: 'Multiple operational modes', category: 'state', isCommitted: true },
  
  // Framework Structures
  { id: 'framework', name: 'Framework (used as a structure)', symbol: '🏗️', description: 'Structural foundation for organization', category: 'framework', isCommitted: true }
];

// Categories for filtering structure types
const structureCategories = [
  { id: 'all', name: 'All Structures', icon: Layers },
  { id: 'hierarchical', name: 'Hierarchical', icon: Target },
  { id: 'network', name: 'Network', icon: Network },
  { id: 'sequential', name: 'Sequential', icon: ArrowRight },
  { id: 'collection', name: 'Collection', icon: Grid3X3 },
  { id: 'matrix', name: 'Matrix', icon: Square },
  { id: 'layered', name: 'Layered', icon: Layers },
  { id: 'state', name: 'State', icon: ToggleLeft },
  { id: 'framework', name: 'Framework', icon: Wrench }
];

// Category Types - Different ways of organizing and presenting content
const categoryTypes = [
  // Conceptual Frameworks
  { id: 'framework', name: 'Framework', symbol: '🏗️', description: 'Structural approach to organizing concepts', category: 'category', isCommitted: true },
  { id: 'model', name: 'Model', symbol: '📊', description: 'Simplified representation of reality', category: 'category', isCommitted: true },
  { id: 'method', name: 'Method', symbol: '🔧', description: 'Systematic approach to achieving goals', category: 'category', isCommitted: true },
  { id: 'methodology', name: 'Methodology', symbol: '📋', description: 'System of methods and principles', category: 'category', isCommitted: true },
  { id: 'tool', name: 'Tool', symbol: '🛠️', description: 'Instrument for accomplishing tasks', category: 'category', isCommitted: true },
  { id: 'technique', name: 'Technique', symbol: '⚡', description: 'Specific way of doing something', category: 'category', isCommitted: true },
  { id: 'principle', name: 'Principle', symbol: '⚖️', description: 'Fundamental truth or proposition', category: 'category', isCommitted: true },
  { id: 'distinction', name: 'Distinction', symbol: '↔️', description: 'Difference or contrast between things', category: 'category', isCommitted: true },
  
  // Knowledge Organization
  { id: 'taxonomy', name: 'Taxonomy', symbol: '🌳', description: 'Classification system', category: 'category', isCommitted: true },
  { id: 'ontology', name: 'Ontology', symbol: '🔍', description: 'Study of being and existence', category: 'category', isCommitted: true },
  { id: 'schema', name: 'Schema', symbol: '📐', description: 'Mental framework for organizing information', category: 'category', isCommitted: true },
  { id: 'structure', name: 'Structure', symbol: '🏛️', description: 'Arrangement of parts in a whole', category: 'category', isCommitted: true },
  { id: 'value', name: 'Value', symbol: '💎', description: 'Worth or importance assigned to something', category: 'category', isCommitted: true },
  { id: 'virtue', name: 'Virtue', symbol: '✨', description: 'Moral excellence or positive quality', category: 'category', isCommitted: true },
  
  // Perspective & Communication
  { id: 'lens', name: 'Lens', symbol: '🔭', description: 'Perspective or viewpoint', category: 'category', isCommitted: true },
  { id: 'perspective', name: 'Perspective', symbol: '👁️', description: 'Point of view or way of seeing', category: 'category', isCommitted: true },
  { id: 'story', name: 'Story', symbol: '📖', description: 'Narrative account of events', category: 'category', isCommitted: true },
  { id: 'narrative', name: 'Narrative', symbol: '📝', description: 'Spoken or written account of events', category: 'category', isCommitted: true },
  { id: 'case', name: 'Case', symbol: '📁', description: 'Specific instance or example', category: 'category', isCommitted: true },
  { id: 'scenario', name: 'Scenario', symbol: '🎭', description: 'Plausible sequence of events', category: 'category', isCommitted: true },
  { id: 'insight', name: 'Insight', symbol: '💡', description: 'Deep understanding or perception', category: 'category', isCommitted: true },
  { id: 'quote', name: 'Quote', symbol: '💬', description: 'Excerpt from a text or speech', category: 'category', isCommitted: true },
  { id: 'aphorism', name: 'Aphorism', symbol: '📜', description: 'Concise statement of principle', category: 'category', isCommitted: true },
  { id: 'example', name: 'Example', symbol: '🔸', description: 'Illustration or instance', category: 'category', isCommitted: true },
  
  // Resources & Tools
  { id: 'resource', name: 'Resource', symbol: '📚', description: 'External source of information', category: 'category', isCommitted: true },
  { id: 'checklist', name: 'Checklist', symbol: '✅', description: 'List of items to be checked', category: 'category', isCommitted: true },
  { id: 'worksheet', name: 'Worksheet', symbol: '📄', description: 'Paper for working on problems', category: 'category', isCommitted: true },
  { id: 'template', name: 'Template', symbol: '📋', description: 'Pre-designed format or pattern', category: 'category', isCommitted: true },
  { id: 'assessment', name: 'Assessment', symbol: '📊', description: 'Evaluation or appraisal', category: 'category', isCommitted: true },
  { id: 'playbook', name: 'Playbook', symbol: '📖', description: 'Strategic guide or manual', category: 'category', isCommitted: true },
  { id: 'guide', name: 'Guide', symbol: '🗺️', description: 'Person or thing that shows the way', category: 'category', isCommitted: true },
  { id: 'glossary', name: 'Glossary', symbol: '📖', description: 'Alphabetical list of terms', category: 'category', isCommitted: true },
  { id: 'dictionary', name: 'Dictionary', symbol: '📚', description: 'Reference work with definitions', category: 'category', isCommitted: true },
  { id: 'card', name: 'Card', symbol: '🃏', description: 'Micro-lesson or small unit', category: 'category', isCommitted: true },
  { id: 'micro-lesson', name: 'Micro-lesson', symbol: '🎓', description: 'Short, focused learning unit', category: 'category', isCommitted: true },
  
  // Practices & Systems
  { id: 'ritual', name: 'Ritual', symbol: '🕯️', description: 'Ceremony or repeated practice', category: 'category', isCommitted: true },
  { id: 'practice', name: 'Practice', symbol: '🔄', description: 'Repeated exercise to improve skill', category: 'category', isCommitted: true },
  { id: 'process', name: 'Process', symbol: '⚙️', description: 'Series of actions or steps', category: 'category', isCommitted: true },
  { id: 'system', name: 'System', symbol: '🔧', description: 'Set of connected parts forming a whole', category: 'category', isCommitted: true }
];

// Category Categories for filtering
const categoryCategories = [
  { id: 'all', name: 'All Categories', icon: Grid3X3 },
  { id: 'conceptual', name: 'Conceptual Frameworks', icon: Target },
  { id: 'knowledge', name: 'Knowledge Organization', icon: BookOpen },
  { id: 'perspective', name: 'Perspective & Communication', icon: Eye },
  { id: 'resources', name: 'Resources & Tools', icon: FileText },
  { id: 'practices', name: 'Practices & Systems', icon: RefreshCw }
];

// Grammar Categories Data (Shape-focused, no color assignments)
const grammarCategories = [
  {
    id: 'ontology',
    name: 'Ontology',
    description: 'What exists - containment, categories, hierarchies',
    primaryShapes: ['nested-rectangles', 'concentric-circles', 'stacked-elements'],
    isCommitted: true
  },
  {
    id: 'modality',
    name: 'Modality', 
    description: 'How things are - spectrum, conditions, states',
    primaryShapes: ['quarter-circle', 'half-circle', 'three-quarter-circle', 'full-circle'],
    isCommitted: true
  },
  {
    id: 'dynamics',
    name: 'Dynamics',
    description: 'How things change - flow, transition, movement',
    primaryShapes: ['curve', 'wavy', 'zigzag', 'overlapping-elements'],
    isCommitted: true
  },
  {
    id: 'evaluation',
    name: 'Evaluation',
    description: 'Why things matter - purpose, targets, assessment',
    primaryShapes: ['ring', 'target', 'radial-grid'],
    isCommitted: true
  }
];

// Semantic Combinations Grouping Data
const semanticGroups = [
  {
    id: 'status-indicators',
    name: 'Status Indicators',
    description: 'Visual representations of completion, progress, and state',
    elements: ['status-complete', 'status-incomplete', 'status-progress', 'status-conditional', 'status-complex'],
    context: 'Process tracking, task management, workflow states'
  },
  {
    id: 'process-indicators',
    name: 'Process Indicators',
    description: 'Visual representations of process flow and completion',
    elements: ['process-complete', 'process-open'],
    context: 'Workflow visualization, progress tracking, system states'
  },
  {
    id: 'relationship-indicators',
    name: 'Relationship Indicators',
    description: 'Visual representations of connections and dependencies',
    elements: ['relationship-strong', 'relationship-conditional', 'relationship-weak'],
    context: 'Network diagrams, dependency mapping, connection visualization'
  }
];

// Encoding System (from Visual Design Grammar)
export const encodingSystem = {
  assumptions: {
    icon: '⚠️',
    meaning: 'Assumptions we tend to make',
    usage: 'Highlight common assumptions that can lead to errors',
    visualMarkers: {
      border: 'border-amber-300 border-dashed',
      background: 'bg-amber-50',
      text: 'text-amber-800'
    }
  },
  looseDefinitions: {
    icon: '💬',
    meaning: 'Words that are loosely defined and prone to misunderstanding',
    usage: 'Flag terms that need clarification or definition',
    visualMarkers: {
      border: 'border-coral-300 border-dotted',
      background: 'bg-coral-50',
      text: 'text-coral-800'
    }
  },
  importantTips: {
    icon: '💡',
    meaning: 'Very important tips or pieces of information',
    usage: 'Highlight critical insights that can make or break success',
    visualMarkers: {
      border: 'border-amber-300 border-2',
      background: 'bg-amber-50',
      text: 'text-amber-800'
    }
  },
  contextMatters: {
    icon: '🧭',
    meaning: 'Context matters here - not the same answer every time',
    usage: 'Indicate situational awareness is required',
    visualMarkers: {
      border: 'border-coral-300 border-dotted',
      background: 'bg-coral-50',
      text: 'text-coral-800'
    }
  },
  dialNotToggle: {
    icon: '🎚️',
    meaning: 'This is really a dial and not a toggle',
    usage: 'Indicate continuous rather than binary states',
    visualMarkers: {
      border: 'border-amber-300 border-2',
      background: 'bg-amber-50',
      text: 'text-amber-800'
    }
  }
};

// The element arrays combined below have three distinct shapes (symbol-based
// shapes/relationships/structures/categories, hex-based colors, fontFamily-based
// fonts). Consumers can't statically know which shape a given element is, so
// variant-specific fields are optional here and must be accessed defensively.
type GeometryElement = {
  id: string;
  name: string;
  description: string;
  symbol?: string;
  category?: string;
  isCommitted?: boolean;
  hex?: string;
  fontFamily?: string;
};

// All Elements Data (for combinations and other views)
const allElements: GeometryElement[] = [
  ...(lineShapeElements || []),
  ...(colorElements || []),
  ...(fontFamilies || []),
  ...(typographyElements || []),
  ...(compositePatterns || []),
  ...(patterns || []),
  ...(semanticCombinations || []),
  ...(relationshipTypes || []),
  ...(structureTypes || []),
  ...(categoryTypes || [])
];

export const GeometryStudio: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  // NOTE: 'colors' is included for the "Color System View" panel below (a
  // ~190-line, fully-built section distinct from the standard 'color-elements'
  // grid), but no tab/button in this file ever calls setViewMode('colors') —
  // that panel is currently unreachable dead code. Kept in the union (rather
  // than deleting the panel or guessing at a UI wiring fix) pending a product
  // decision on whether it should get its own tab or be removed.
  const [viewMode, setViewMode] = useState<'all' | 'line-shape-elements' | 'color-elements' | 'colors' | 'fonts' | 'typography' | 'relationships' | 'structures' | 'categories' | 'grammar' | 'combinations' | 'builder' | 'semantic'>('all');
  const [combinationView, setCombinationView] = useState<'list' | 'grouped'>('list');
  const [builtCombination, setBuiltCombination] = useState<any>(null);

  // Get current elements and categories based on view mode
  const currentElements = useMemo<GeometryElement[]>(() => {
    switch (viewMode) {
      case 'all':
        return allElements;
      case 'line-shape-elements':
        return lineShapeElements;
      case 'color-elements':
        return colorElements;
      case 'fonts':
        return fontFamilies;
      case 'typography':
        return typographyElements;
      case 'relationships':
        return relationshipTypes;
      case 'structures':
        return structureTypes;
      case 'categories':
        return categoryTypes;
      default:
        return allElements;
    }
  }, [viewMode]);

  const currentCategories = useMemo(() => {
    switch (viewMode) {
      case 'all':
        return [
          { id: 'all', name: 'All Elements', icon: Shapes },
          { id: 'shape', name: 'Shapes', icon: Circle },
          { id: 'neutral', name: 'Neutral Colors', icon: Palette },
          { id: 'brand', name: 'Brand Colors', icon: Palette },
          { id: 'complement', name: 'Complementary Colors', icon: Palette },
          { id: 'structural', name: 'Structural Colors', icon: Palette },
          { id: 'family', name: 'Font Families', icon: FileText },
          { id: 'weight', name: 'Font Weights', icon: Square },
          { id: 'style', name: 'Font Styles', icon: Minus },
          { id: 'size', name: 'Font Sizes', icon: Target },
          { id: 'decoration', name: 'Text Decorations', icon: Network },
          { id: 'spacing', name: 'Letter Spacing', icon: Grid3X3 },
          { id: 'lineHeight', name: 'Line Heights', icon: Zap },
          { id: 'line', name: 'Line Styles', icon: Minus },
          { id: 'fill', name: 'Fill Patterns', icon: Square },
          { id: 'modifier', name: 'Shape Modifiers', icon: RotateCw },
          { id: 'composite', name: 'Composite Patterns', icon: Grid3X3 },
          { id: 'pattern', name: 'Patterns', icon: Grid3X3 },
          { id: 'connection', name: 'Connections', icon: Network },
          { id: 'semantic-combination', name: 'Semantic Combinations', icon: Target },
          { id: 'hierarchical', name: 'Hierarchical Relationships', icon: Target },
          { id: 'functional', name: 'Functional Relationships', icon: Zap },
          { id: 'semantic', name: 'Semantic Relationships', icon: Circle },
          { id: 'explanatory', name: 'Explanatory Relationships', icon: Square },
          { id: 'referential', name: 'Referential Relationships', icon: Network },
          { id: 'temporal', name: 'Temporal Relationships', icon: Minus },
          { id: 'structural-relationship', name: 'Structural Relationships', icon: Grid3X3 },
          { id: 'hierarchical-structure', name: 'Hierarchical Structures', icon: Target },
          { id: 'network-structure', name: 'Network Structures', icon: Network },
          { id: 'sequential-structure', name: 'Sequential Structures', icon: ArrowRight },
          { id: 'collection-structure', name: 'Collection Structures', icon: Grid3X3 },
          { id: 'matrix-structure', name: 'Matrix Structures', icon: Square },
          { id: 'layered-structure', name: 'Layered Structures', icon: Layers },
          { id: 'state-structure', name: 'State Structures', icon: ToggleLeft },
          { id: 'framework-structure', name: 'Framework Structures', icon: Wrench },
          { id: 'conceptual', name: 'Conceptual Frameworks', icon: Target },
          { id: 'knowledge', name: 'Knowledge Organization', icon: BookOpen },
          { id: 'perspective', name: 'Perspective & Communication', icon: Eye },
          { id: 'resources', name: 'Resources & Tools', icon: FileText },
          { id: 'practices', name: 'Practices & Systems', icon: RefreshCw }
        ];
      case 'line-shape-elements':
        return lineShapeCategories;
      case 'color-elements':
        return colorCategories;
      case 'fonts':
        return fontCategories;
      case 'typography':
        return typographyCategories;
      case 'relationships':
        return relationshipCategories;
      case 'structures':
        return structureCategories;
      case 'categories':
        return categoryCategories;
      default:
        return lineShapeCategories;
    }
  }, [viewMode]);

  // Filter elements based on search and category
  const filteredElements = useMemo(() => {
    return currentElements.filter(element => {
      const matchesSearch = element.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           element.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (element.symbol?.includes(searchTerm) ?? false);
      const matchesCategory = selectedCategory === 'all' || element.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [currentElements, searchTerm, selectedCategory]);

  const toggleElement = (elementId: string) => {
    setSelectedElements(prev => 
      prev.includes(elementId) 
        ? prev.filter(id => id !== elementId)
        : [...prev, elementId]
    );
  };

  const getSelectedElementsData = () => {
    return allElements.filter(element => selectedElements.includes(element.id));
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = currentCategories.find(cat => cat.id === categoryId);
    return category ? category.icon : Shapes;
  };

  const getCategoryName = (categoryId: string) => {
    const category = currentCategories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  // Encoding function to generate semantic meaning from built combinations
  const encodeCombination = (elements: any[]) => {
    if (elements.length === 0) return null;
    
    const combination = {
      id: `combination-${Date.now()}`,
      name: 'Custom Combination',
      elements: elements,
      semanticMeaning: generateSemanticMeaning(elements),
      encoding: generateEncoding(elements),
      timestamp: new Date().toISOString()
    };
    
    setBuiltCombination(combination);
    return combination;
  };

  const generateSemanticMeaning = (elements: any[]) => {
    const meanings = elements.map(el => el.description).join(' + ');
    return `Combination of: ${meanings}`;
  };

  const generateEncoding = (elements: any[]) => {
    // Simple encoding based on element types
    const hasShape = elements.some(el => el.category === 'shape');
    const hasColor = elements.some(el => el.category === 'color');
    const hasLine = elements.some(el => el.category === 'line');
    const hasPattern = elements.some(el => el.category === 'pattern');
    
    let encoding = 'Basic Visual Element';
    if (hasShape && hasColor) encoding = 'Colored Shape';
    if (hasShape && hasColor && hasLine) encoding = 'Connected Colored Shape';
    if (hasPattern) encoding = 'Pattern-Based Element';
    
    return encoding;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to The Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 rounded-lg font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Lab
          </div>
          <Shapes className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Geometry Studio</h1>
            <p className="text-charcoal-600">Visual Semantic Vocabulary Builder</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Build and explore the complete visual semantic vocabulary for Peak Priorities. 
          Use faceted search to discover shapes, lines, fills, strokes, and semantic combinations 
          that encode meaning through geometry rather than illustration.
        </p>
      </div>

      {/* View Mode Toggle */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal-900">Visual Semantic Vocabulary</h2>
          <div className="flex bg-charcoal-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode('line-shape-elements')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'line-shape-elements'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Line/Shape Elements
            </button>
            <button
              onClick={() => setViewMode('color-elements')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'color-elements'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Color Elements
            </button>
            <button
              onClick={() => setViewMode('fonts')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'fonts'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Fonts
            </button>
            <button
              onClick={() => setViewMode('typography')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'typography'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Typography
            </button>
            <button
              onClick={() => setViewMode('relationships')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'relationships'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Relationships
            </button>
            <button
              onClick={() => setViewMode('structures')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'structures'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Structures
            </button>
            <button
              onClick={() => setViewMode('categories')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'categories'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Categories
            </button>
            <button
              onClick={() => setViewMode('grammar')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grammar'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Grammar
            </button>
            <button
              onClick={() => setViewMode('combinations')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'combinations'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Combinations
            </button>
            <button
              onClick={() => setViewMode('builder')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'builder'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Builder
            </button>
            <button
              onClick={() => setViewMode('semantic')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'semantic'
                  ? 'bg-white text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              Semantic
            </button>
          </div>
        </div>

        {(viewMode === 'all' || viewMode === 'line-shape-elements' || viewMode === 'color-elements' || viewMode === 'fonts' || viewMode === 'typography' || viewMode === 'relationships' || viewMode === 'structures' || viewMode === 'categories') && (
          <>
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
                  <input
                    type="text"
                    placeholder="Search elements by name, symbol, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-charcoal-300 rounded-lg focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Category Filter */}
              <div className="lg:w-64">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-charcoal-300 rounded-lg focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    {currentCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {currentCategories.map(category => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedCategory === category.id
                        ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50 text-consulting-slate-blue-700'
                        : 'border-charcoal-200 bg-white text-charcoal-600 hover:border-charcoal-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{category.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Results Count */}
            <div className="text-sm text-charcoal-600 mb-4">
              Showing {filteredElements.length} of {allElements.length} elements
            </div>
          </>
        )}
      </div>

      {/* Selected Elements Summary */}
      {selectedElements.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-amber-900 mb-3">
            Selected Elements ({selectedElements.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {getSelectedElementsData().map(element => {
              const Icon = getCategoryIcon(element.category ?? '');
              return (
                <div
                  key={element.id}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-amber-200"
                >
                  <Icon className="h-4 w-4 text-amber-600" />
                  <span className="text-lg font-mono">{element.symbol}</span>
                  <span className="text-sm font-medium text-amber-800">{element.name}</span>
                  <button
                    onClick={() => toggleElement(element.id)}
                    className="text-amber-600 hover:text-amber-800"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grammar Categories View */}
             {viewMode === 'grammar' && (
               <div className="space-y-6">
                 <div className="bg-charcoal-50 rounded-lg p-4 border border-charcoal-200">
                   <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Grammar Categories</h3>
                   <p className="text-sm text-charcoal-600">
                     These categories define the structural grammar of visual elements. Colors can be applied to any category based on context and need.
                   </p>
                 </div>
                 
                 <div className="grid md:grid-cols-2 gap-6">
                   {grammarCategories.map(category => {
                     const opacity = category.isCommitted ? '' : 'opacity-60';
                     
                     return (
                       <div
                         key={category.id}
                         className={`p-6 rounded-xl border-2 border-charcoal-200 bg-white transition-all hover:shadow-lg hover:border-charcoal-300 ${opacity}`}
                       >
                         <div className="flex items-start justify-between mb-4">
                           <h3 className={`text-xl font-semibold text-charcoal-900 ${opacity}`}>
                             {category.name}
                           </h3>
                           <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                             category.isCommitted 
                               ? 'bg-charcoal-100 text-charcoal-700' 
                               : 'bg-amber-100 text-amber-700'
                           }`}>
                             {category.isCommitted ? 'Committed' : 'Draft'}
                           </div>
                         </div>
                         
                         <p className={`text-sm leading-relaxed mb-4 text-charcoal-600 ${opacity}`}>
                           {category.description}
                         </p>
                         
                         <div>
                           <h4 className={`text-sm font-medium mb-2 text-charcoal-700 ${opacity}`}>
                             Primary Shapes:
                           </h4>
                           <div className="flex flex-wrap gap-2">
                             {category.primaryShapes.map(shapeId => {
                               const shape = allElements.find(el => el.id === shapeId);
                               if (!shape) return null;
                               return (
                                 <div
                                   key={shapeId}
                                   className={`flex items-center gap-2 px-3 py-1 rounded-lg border border-charcoal-200 bg-charcoal-50 ${opacity}`}
                                 >
                                   <span className="text-lg font-mono">{shape.symbol}</span>
                                   <span className={`text-xs font-medium text-charcoal-700 ${opacity}`}>
                                     {shape.name}
                                   </span>
                                 </div>
                               );
                             })}
                           </div>
                         </div>
                       </div>
                     );
                   })}
                 </div>
               </div>
             )}

      {/* Semantic Combinations View */}
      {viewMode === 'combinations' && (
        <div className="space-y-6">
          {/* View Toggle for Combinations */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-charcoal-900">Semantic Combinations</h3>
            <div className="flex bg-charcoal-100 rounded-lg p-1">
              <button
                onClick={() => setCombinationView('list')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  combinationView === 'list'
                    ? 'bg-white text-charcoal-900 shadow-sm'
                    : 'text-charcoal-600 hover:text-charcoal-900'
                }`}
              >
                List View
              </button>
              <button
                onClick={() => setCombinationView('grouped')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  combinationView === 'grouped'
                    ? 'bg-white text-charcoal-900 shadow-sm'
                    : 'text-charcoal-600 hover:text-charcoal-900'
                }`}
              >
                Grouped by Context
              </button>
            </div>
          </div>

          {combinationView === 'list' ? (
            /* List View - All Semantic Combinations */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {semanticCombinations.map(element => {
                const isCommitted = element.isCommitted;
                const opacity = isCommitted ? '' : 'opacity-60';
                
                return (
                  <div
                    key={element.id}
                    className={`p-4 rounded-xl border-2 bg-white border-charcoal-200 hover:border-charcoal-300 transition-all hover:shadow-lg ${opacity}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-charcoal-500" />
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-charcoal-100 text-charcoal-600">
                          Semantic
                        </span>
                        {!isCommitted && (
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                            Draft
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-center mb-3">
                      <div className="text-4xl font-mono mb-2">{element.symbol}</div>
                      <h3 className="font-semibold text-charcoal-900">
                        {element.name}
                      </h3>
                    </div>
                    
                    <p className="text-sm leading-relaxed text-charcoal-600">
                      {element.description}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Grouped View - By Context */
            <div className="space-y-6">
              {semanticGroups.map(group => (
                <div key={group.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-charcoal-900 mb-2">
                      {group.name}
                    </h3>
                    <p className="text-charcoal-600 mb-2">{group.description}</p>
                    <p className="text-sm text-charcoal-500">
                      <strong>Context:</strong> {group.context}
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.elements.map(elementId => {
                      const element = semanticCombinations.find(el => el.id === elementId);
                      if (!element) return null;
                      
                      return (
                        <div
                          key={elementId}
                          className="p-4 rounded-lg border border-charcoal-200 bg-charcoal-50"
                        >
                          <div className="text-center mb-3">
                            <div className="text-3xl font-mono mb-2">{element.symbol}</div>
                            <h4 className="font-medium text-charcoal-900">
                              {element.name}
                            </h4>
                          </div>
                          <p className="text-sm text-charcoal-600">
                            {element.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
             )}

      {/* Color System View */}
      {viewMode === 'colors' && (
        <div className="space-y-6">
          <div className="bg-charcoal-50 rounded-lg p-4 border border-charcoal-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Universal Color System</h3>
            <p className="text-sm text-charcoal-600">
              Colors are a universal design tool that can be applied to any element, shape, or pattern. 
              Choose colors based on context, meaning, and visual hierarchy rather than being constrained by grammar categories.
            </p>
          </div>
          
          <div className="space-y-8">
            {/* Neutral Colors */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Neutral Colors</h3>
                <p className="text-sm text-charcoal-600">Structural and functional colors for hierarchy and organization</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {colorSystem.neutral.map(color => (
                  <div key={color.id} className="p-4 rounded-lg border-2 border-charcoal-200 bg-white hover:border-charcoal-300 transition-all hover:shadow-lg">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-3" style={{ backgroundColor: color.hex }}></div>
                      <h4 className="font-semibold text-charcoal-900">{color.name}</h4>
                      <div className="text-xs text-charcoal-500 mt-1">{color.hex}</div>
                    </div>
                    <p className="text-xs leading-relaxed text-charcoal-600">{color.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Brand Colors */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Brand Colors</h3>
                <p className="text-sm text-charcoal-600">Brand identity colors for recognition and consistency</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {colorSystem.brand.map(color => (
                  <div key={color.id} className="p-4 rounded-lg border-2 border-charcoal-200 bg-white hover:border-charcoal-300 transition-all hover:shadow-lg">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-3" style={{ backgroundColor: color.hex }}></div>
                      <h4 className="font-semibold text-charcoal-900">{color.name}</h4>
                      <div className="text-xs text-charcoal-500 mt-1">{color.hex}</div>
                    </div>
                    <p className="text-xs leading-relaxed text-charcoal-600">{color.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Color Ramps */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Color Ramps</h3>
                <p className="text-sm text-charcoal-600">Full tonal ranges for all colors (50-900 scale)</p>
              </div>
              <div className="space-y-8">
                {/* Brand Color Ramps */}
                <div>
                  <h4 className="text-lg font-medium text-charcoal-800 mb-4">Brand Color Ramps</h4>
                  <div className="space-y-6">
                    {Object.entries(colorSystem.ramps).filter(([key]) => ['primary', 'sage', 'amber', 'coral', 'brown'].includes(key)).map(([rampKey, rampColors]) => (
                      <div key={rampKey}>
                        <h5 className="text-md font-medium text-charcoal-700 mb-3 capitalize">{rampKey} Ramp</h5>
                        <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                          {rampColors.map(color => (
                            <div key={color.id} className="text-center">
                              <div className="w-12 h-12 rounded-lg border border-charcoal-300 mx-auto mb-2" style={{ backgroundColor: color.hex }}></div>
                              <div className="text-xs font-medium text-charcoal-700">{color.name.split(' ')[1]}</div>
                              <div className="text-xs text-charcoal-500">{color.hex}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Complementary Color Ramps */}
                <div>
                  <h4 className="text-lg font-medium text-charcoal-800 mb-4">Complementary Color Ramps</h4>
                  <div className="space-y-6">
                    {Object.entries(colorSystem.ramps).filter(([key]) => key.startsWith('complement')).map(([rampKey, rampColors]) => (
                      <div key={rampKey}>
                        <h5 className="text-md font-medium text-charcoal-700 mb-3 capitalize">{rampKey.replace('complement', '').replace(/([A-Z])/g, ' $1').trim()} Ramp</h5>
                        <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                          {rampColors.map(color => (
                            <div key={color.id} className="text-center">
                              <div className="w-12 h-12 rounded-lg border border-charcoal-300 mx-auto mb-2" style={{ backgroundColor: color.hex }}></div>
                              <div className="text-xs font-medium text-charcoal-700">{color.name.split(' ')[1]}</div>
                              <div className="text-xs text-charcoal-500">{color.hex}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Structural Gap Filler Ramps */}
                <div>
                  <h4 className="text-lg font-medium text-charcoal-800 mb-4">Structural Gap Filler Ramps</h4>
                  <div className="space-y-6">
                    {Object.entries(colorSystem.ramps).filter(([key]) => ['warmGray', 'coolGray', 'midBlue', 'midGreen', 'midYellow'].includes(key)).map(([rampKey, rampColors]) => (
                      <div key={rampKey}>
                        <h5 className="text-md font-medium text-charcoal-700 mb-3 capitalize">{rampKey.replace(/([A-Z])/g, ' $1').trim()} Ramp</h5>
                        <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                          {rampColors.map(color => (
                            <div key={color.id} className="text-center">
                              <div className="w-12 h-12 rounded-lg border border-charcoal-300 mx-auto mb-2" style={{ backgroundColor: color.hex }}></div>
                              <div className="text-xs font-medium text-charcoal-700">{color.name.split(' ')[1]}</div>
                              <div className="text-xs text-charcoal-500">{color.hex}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Complementary Colors */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Complementary Colors</h3>
                <p className="text-sm text-charcoal-600">Color theory based complements for visual harmony and contrast</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {colorSystem.complements.map(color => (
                  <div key={color.id} className="p-4 rounded-lg border-2 border-charcoal-200 bg-white hover:border-charcoal-300 transition-all hover:shadow-lg">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-3" style={{ backgroundColor: color.hex }}></div>
                      <h4 className="font-semibold text-charcoal-900">{color.name}</h4>
                      <div className="text-xs text-charcoal-500 mt-1">{color.hex}</div>
                    </div>
                    <p className="text-xs leading-relaxed text-charcoal-600">{color.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Structural Gap Fillers */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Structural Gap Fillers</h3>
                <p className="text-sm text-charcoal-600">Colors that bridge gaps in the palette for smooth transitions</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {colorSystem.structural.map(color => (
                  <div key={color.id} className="p-4 rounded-lg border-2 border-charcoal-200 bg-white hover:border-charcoal-300 transition-all hover:shadow-lg">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-3" style={{ backgroundColor: color.hex }}></div>
                      <h4 className="font-semibold text-charcoal-900">{color.name}</h4>
                      <div className="text-xs text-charcoal-500 mt-1">{color.hex}</div>
                    </div>
                    <p className="text-xs leading-relaxed text-charcoal-600">{color.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="bg-sage-50 rounded-lg p-6 border border-sage-200">
            <h4 className="text-lg font-semibold text-sage-900 mb-3">Color Application Guidelines</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-sage-800 mb-2">Context-Based Application</h5>
                <ul className="text-sm text-sage-700 space-y-1">
                  <li>• Use neutral colors for structure and hierarchy</li>
                  <li>• Apply brand colors for identity and recognition</li>
                  <li>• Use semantic colors for meaning and states</li>
                  <li>• Combine colors based on visual needs, not grammar rules</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-sage-800 mb-2">Flexible Usage</h5>
                <ul className="text-sm text-sage-700 space-y-1">
                  <li>• Any color can be applied to any shape or pattern</li>
                  <li>• Colors work across all MOF categories</li>
                  <li>• Choose colors based on context and purpose</li>
                  <li>• Maintain consistency within specific use cases</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shape Builder View */}
             {viewMode === 'builder' && (
               <div className="space-y-6">
                 <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                   <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Shape Builder</h3>
                   <p className="text-charcoal-600 mb-6">
                     Combine different elements to create new semantic meanings. This is your toolbox for building 
                     visual vocabulary on top of visual grammar.
                   </p>
                   
                   {/* Builder Canvas */}
                   <div className="bg-charcoal-50 rounded-lg border-2 border-dashed border-charcoal-300 p-8 min-h-64 flex items-center justify-center">
                     <div className="text-center">
                       <Shapes className="h-12 w-12 text-charcoal-400 mx-auto mb-4" />
                       <p className="text-charcoal-500 mb-2">Shape Builder Canvas</p>
                       <p className="text-sm text-charcoal-400">
                         Select elements from the toolbox below to build combinations
                       </p>
                       {selectedElements.length > 0 && (
                         <div className="mt-4">
                           <button
                             onClick={() => encodeCombination(getSelectedElementsData())}
                             className="px-4 py-2 bg-consulting-slate-blue-600 text-white rounded-lg hover:bg-consulting-slate-blue-700 transition-colors"
                           >
                             Encode Combination
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                   
                   {/* Toolbox */}
                   <div className="mt-6">
                     <h4 className="text-md font-medium text-charcoal-900 mb-3">Toolbox</h4>
                     <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-2">
                       {allElements.slice(0, 24).map(element => {
                         const isSelected = selectedElements.includes(element.id);
                         return (
                           <div
                             key={element.id}
                             onClick={() => toggleElement(element.id)}
                             className={`p-2 rounded-lg border transition-all cursor-pointer ${
                               isSelected 
                                 ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50' 
                                 : 'border-charcoal-200 bg-white hover:border-consulting-slate-blue-300 hover:bg-consulting-slate-blue-50'
                             }`}
                             title={element.name}
                           >
                             <div className="text-center">
                               <div className="text-lg font-mono mb-1">{element.symbol}</div>
                               <div className="text-xs text-charcoal-600 truncate">
                                 {element.name}
                               </div>
                             </div>
                           </div>
                         );
                       })}
                     </div>
                   </div>
                   
                   {/* Built Combination Display */}
                   {builtCombination && (
                     <div className="mt-6 p-4 bg-sage-50 border border-sage-200 rounded-lg">
                       <h4 className="text-sm font-medium text-sage-900 mb-3">Encoded Combination</h4>
                       <div className="space-y-2">
                         <div className="flex items-center gap-2">
                           <span className="text-sm font-medium text-sage-800">Elements:</span>
                           <div className="flex gap-1">
                             {builtCombination.elements.map((element: any) => (
                               <span key={element.id} className="text-lg font-mono" title={element.name}>
                                 {element.symbol}
                               </span>
                             ))}
                           </div>
                         </div>
                         <div className="text-sm text-sage-700">
                           <strong>Semantic Meaning:</strong> {builtCombination.semanticMeaning}
                         </div>
                         <div className="text-sm text-sage-700">
                           <strong>Encoding:</strong> {builtCombination.encoding}
                         </div>
                         <div className="text-xs text-sage-600">
                           Generated: {new Date(builtCombination.timestamp).toLocaleString()}
                         </div>
                       </div>
                     </div>
                   )}
                   
                   {/* Future Features Note */}
                   <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                     <h4 className="text-sm font-medium text-amber-900 mb-2">Coming Soon</h4>
                     <ul className="text-sm text-amber-800 space-y-1">
                       <li>• Drag and drop functionality</li>
                       <li>• Real-time combination testing</li>
                       <li>• Advanced semantic meaning validation</li>
                       <li>• Export combinations as reusable components</li>
                       <li>• Cross-scenario testing framework</li>
                     </ul>
                   </div>
                 </div>
               </div>
             )}

      {/* Relationships View - Special Section */}
      {viewMode === 'relationships' && (
        <div className="space-y-6">
          <div className="bg-charcoal-50 rounded-lg p-4 border border-charcoal-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Relationship Encoding System</h3>
            <p className="text-sm text-charcoal-600 mb-3">
              These relationship types are organized into seven categories that correspond to the M3 Ontology relationships. 
              Each relationship type has a unique symbol and semantic meaning for visual encoding.
            </p>
            <div className="flex items-center gap-2 text-sm text-consulting-slate-blue-600">
              <Link to="/elevation/grammar" className="hover:text-consulting-slate-blue-700 underline">
                View M3 Ontology Relationships →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Structures View - Special Section */}
      {viewMode === 'structures' && (
        <div className="space-y-6">
          <div className="bg-charcoal-50 rounded-lg p-4 border border-charcoal-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Visual Organization Structures</h3>
            <p className="text-sm text-charcoal-600 mb-3">
              These structure types represent different ways of organizing and presenting information visually. 
              Each structure has a specific purpose and is suited for different types of content and user interactions.
            </p>
            <p className="text-xs text-charcoal-500 italic">
              Use these structures to determine the best way to organize your content based on its nature and purpose.
            </p>
          </div>
        </div>
      )}

      {/* Categories View - Special Section */}
      {viewMode === 'categories' && (
        <div className="space-y-6">
          <div className="bg-charcoal-50 rounded-lg p-4 border border-charcoal-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Content Organization Categories</h3>
            <p className="text-sm text-charcoal-600 mb-3">
              These category types represent different ways of organizing and presenting content. 
              Each category type serves a specific purpose in how information is structured, communicated, and consumed.
            </p>
            <p className="text-xs text-charcoal-500 italic">
              Use these categories to determine the most appropriate way to organize your content based on its nature, purpose, and audience.
            </p>
          </div>
        </div>
      )}

      {/* Elements Grid */}
      {(viewMode === 'all' || viewMode === 'line-shape-elements' || viewMode === 'color-elements' || viewMode === 'fonts' || viewMode === 'typography' || viewMode === 'relationships' || viewMode === 'structures' || viewMode === 'categories') && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredElements.map(element => {
            const Icon = getCategoryIcon(element.category ?? '');
            const isSelected = selectedElements.includes(element.id);
            const isCommitted = element.isCommitted;
            const opacity = isCommitted ? '' : 'opacity-60';
            
            return (
              <div
                key={element.id}
                onClick={() => toggleElement(element.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${opacity} ${
                  isSelected
                    ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50'
                    : 'border-charcoal-200 bg-white hover:border-charcoal-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-consulting-slate-blue-600' : 'text-charcoal-500'}`} />
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      isSelected 
                        ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700' 
                        : 'bg-charcoal-100 text-charcoal-600'
                    }`}>
                      {getCategoryName(element.category ?? '')}
                    </span>
                    {!isCommitted && (
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                        Draft
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 bg-consulting-slate-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                
                       <div className="text-center mb-3">
                         {(element.category === 'neutral' || element.category === 'brand' || element.category === 'complement' || element.category === 'structural') ? (
                           <div 
                             className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-2"
                             style={{ backgroundColor: element.hex }}
                           ></div>
                         ) : element.category === 'font' ? (
                           <div 
                             className="w-16 h-16 rounded-lg border-2 border-charcoal-300 mx-auto mb-2 flex items-center justify-center bg-white"
                             style={{ fontFamily: element.fontFamily }}
                           >
                             <span className="text-2xl font-bold">Aa</span>
                           </div>
                         ) : (
                           <div className="text-4xl font-mono mb-2">{element.symbol}</div>
                         )}
                         <h3 className={`font-semibold ${isSelected ? 'text-consulting-slate-blue-900' : 'text-charcoal-900'}`}>
                           {element.name}
                         </h3>
                       </div>
                
                <p className={`text-sm leading-relaxed ${isSelected ? 'text-consulting-slate-blue-700' : 'text-charcoal-600'}`}>
                  {element.description}
                </p>
              </div>
            );
          })}
        </div>
        )}

        {/* Semantic View - Color information and semantic combinations */}
        {viewMode === 'semantic' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Semantic Colors</h3>
              <p className="text-sm text-charcoal-600 mb-4">
                Colors applied after encoding combinations to convey meaning and context. 
                These are the final semantic layer that gives visual meaning to your encoded combinations.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: '#53675B' }}></div>
                    <h4 className="font-semibold" style={{ color: '#53675B' }}>Success States</h4>
                  </div>
                  <p className="text-sm" style={{ color: '#53675B' }}>Applied to combinations that represent positive outcomes, completion, or successful states.</p>
                  <div className="mt-2 text-xs" style={{ color: '#53675B' }}>Examples: Completed processes, achieved goals, positive feedback</div>
                </div>
                
                <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: '#E3B505' }}></div>
                    <h4 className="font-semibold" style={{ color: '#E3B505' }}>Warning States</h4>
                  </div>
                  <p className="text-sm" style={{ color: '#E3B505' }}>Applied to combinations that require attention, caution, or intermediate states.</p>
                  <div className="mt-2 text-xs" style={{ color: '#E3B505' }}>Examples: Pending actions, review needed, moderate risk</div>
                </div>
                
                <div className="p-4 rounded-lg border-2 border-red-200 bg-red-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: '#F85E5E' }}></div>
                    <h4 className="font-semibold" style={{ color: '#F85E5E' }}>Error States</h4>
                  </div>
                  <p className="text-sm" style={{ color: '#F85E5E' }}>Applied to combinations that represent problems, failures, or critical issues.</p>
                  <div className="mt-2 text-xs" style={{ color: '#F85E5E' }}>Examples: Failed processes, critical errors, blocked states</div>
                </div>
                
                <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: '#5A7D5E' }}></div>
                    <h4 className="font-semibold" style={{ color: '#5A7D5E' }}>Information States</h4>
                  </div>
                  <p className="text-sm" style={{ color: '#5A7D5E' }}>Applied to combinations that provide information or neutral states.</p>
                  <div className="mt-2 text-xs" style={{ color: '#5A7D5E' }}>Examples: Data display, neutral processes, informational content</div>
                </div>
                
                <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500"></div>
                    <h4 className="font-semibold text-purple-800">Muted States</h4>
                  </div>
                  <p className="text-sm text-purple-700">Applied to combinations that are secondary, subtle, or less prominent.</p>
                  <div className="mt-2 text-xs text-purple-600">Examples: Secondary information, background elements, subtle indicators</div>
                </div>
                
                <div className="p-4 rounded-lg border-2 border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-500"></div>
                    <h4 className="font-semibold text-gray-800">Neutral States</h4>
                  </div>
                  <p className="text-sm text-gray-700">Applied to combinations that are structural or have no specific semantic meaning.</p>
                  <div className="mt-2 text-xs text-gray-600">Examples: Framework elements, structural components, base states</div>
                </div>
              </div>
            </div>
            
            <div className="bg-sage-50 rounded-lg p-6 border border-sage-200">
              <h4 className="text-lg font-semibold text-sage-900 mb-3">Semantic Color Application Process</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-sage-800 mb-2">1. Encode First</h5>
                  <p className="text-sm text-sage-700 mb-3">Use the Shape Builder to create and encode your visual combinations.</p>
                  
                  <h5 className="font-medium text-sage-800 mb-2">2. Apply Semantic Meaning</h5>
                  <p className="text-sm text-sage-700 mb-3">Based on the encoded meaning, choose appropriate semantic colors.</p>
                </div>
                <div>
                  <h5 className="font-medium text-sage-800 mb-2">3. Context Matters</h5>
                  <p className="text-sm text-sage-700 mb-3">The same combination might have different semantic colors in different contexts.</p>
                  
                  <h5 className="font-medium text-sage-800 mb-2">4. Consistency</h5>
                  <p className="text-sm text-sage-700">Maintain consistent semantic color usage within your system or project.</p>
                </div>
              </div>
            </div>
            
            {/* Semantic Combinations */}
            <div className="bg-white rounded-xl border-2 border-charcoal-200 p-6">
              <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Semantic Combinations</h3>
              <p className="text-sm text-charcoal-600 mb-4">
                Pre-defined combinations that carry specific semantic meaning and can be used as building blocks for more complex visual expressions.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {semanticCombinations.map(combination => (
                  <div key={combination.id} className="p-4 rounded-lg border-2 border-charcoal-200 bg-white hover:border-charcoal-300 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{combination.symbol}</span>
                      <div>
                        <h4 className="font-semibold text-charcoal-900">{combination.name}</h4>
                        <div className="text-xs text-charcoal-500 capitalize">{combination.category}</div>
                      </div>
                    </div>
                    <p className="text-sm text-charcoal-600">{combination.description}</p>
                    {!combination.isCommitted && (
                      <div className="mt-2">
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                          Draft
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Usage Guidelines */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Usage Guidelines</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Core Principles</h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Structure over Illustration:</strong> Shapes encode meaning through geometry, not pictorial representation</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Minimal Primitives:</strong> Small set of base forms, combined consistently</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Semantic Combinations:</strong> How shapes combine carries meaning</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Anti-Icon Rule:</strong> Avoid pictorial/literal icons; prefer abstract forms</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Priority-Based Assignment</h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-sage-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Priority 1:</strong> Core canon (MOF layers, DEGAP, Capability Model)</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Priority 2:</strong> Domain structuring (System Modeling, Rules Grammar)</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-coral-500 rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Priority 3:</strong> Operational (Scorecards, {UI_LABELS.itemPools}, Templates)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Future Features */}
      <div className="bg-charcoal-50 border-2 border-charcoal-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Coming Soon</h3>
        <div className="grid md:grid-cols-2 gap-4 text-charcoal-700">
          <div>
            <h4 className="font-medium mb-2">Advanced Features</h4>
            <ul className="space-y-1 text-sm">
              <li>• Custom element creation and combination</li>
              <li>• Export selected elements as SVG libraries</li>
              <li>• Semantic meaning derivation tools</li>
              <li>• Integration with Icon Customizer</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Analysis Tools</h4>
            <ul className="space-y-1 text-sm">
              <li>• Visual consistency checker</li>
              <li>• Semantic meaning validator</li>
              <li>• Usage pattern analysis</li>
              <li>• Priority-based assignment helper</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

